import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, RotateCcw, Save, ShieldCheck, X } from "lucide-react";
import { apiGet, apiPost, apiPut } from "../../utils/api";
import { getPlanDisplayName, getPlanIdentity, getPlanPublicId } from "../../utils/pricingDisplay.js";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

const PLAN_ORDER = ["free", "essentials", "basic", "enterprise"];
const PAISE_PER_UNIT = 100;

function paiseToUnits(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return (numeric / PAISE_PER_UNIT).toFixed(2);
}

function sanitizeCurrency(rawValue) {
  return String(rawValue || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
}

function normalizeSeatCountInput(rawValue) {
  return String(rawValue || "").replace(/[^\d]/g, "").slice(0, 6);
}

function normalizeIntegerInput(rawValue, maxLength = 6) {
  return String(rawValue || "").replace(/[^\d]/g, "").slice(0, maxLength);
}

function normalizeShortText(rawValue, maxLength = 120) {
  return String(rawValue ?? "").slice(0, maxLength);
}

function normalizeLongText(rawValue, maxLength = 260) {
  return String(rawValue ?? "").slice(0, maxLength);
}

function toTitleFromId(value) {
  return String(value || "")
    .split(/[_\s-]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatPlanPrice(priceInPaise, currency = "INR", contactSales = false) {
  if (contactSales) return "Contact Sales";
  const numeric = Number(priceInPaise);
  if (!Number.isFinite(numeric) || numeric <= 0) return "Free";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(numeric / 100);
  } catch {
    return `${(numeric / 100).toFixed(2)} ${currency}`;
  }
}

function isFreePlan(planId) {
  return String(planId || "").trim().toLowerCase() === "free";
}

function isEnterprisePlan(planId) {
  return String(planId || "").trim().toLowerCase() === "enterprise";
}

function isPaidSelfServePlan(planId) {
  const normalized = String(planId || "").trim().toLowerCase();
  return normalized === "essentials" || normalized === "basic";
}

function getPlanModeLabel(planId, requiresPayment, contactSales) {
  if (isFreePlan(planId)) return "Free Claim";
  if (contactSales) return "Tenant Quote";
  if (requiresPayment) return "Razorpay Checkout";
  return "Manual Plan";
}

function getSeatCapLabel(maxSystems) {
  return maxSystems == null ? "Unlimited" : `${maxSystems} seats`;
}

function normalizePlanRows(payload) {
  const rows = Array.isArray(payload?.catalog) ? payload.catalog : [];
  const normalized = rows.map((row) => ({
    id: String(row?.id || "").trim().toLowerCase(),
    publicPlanId: getPlanPublicId(row?.id),
    displayName: getPlanDisplayName(row?.id, row?.name),
    name: getPlanDisplayName(row?.id, row?.name),
    description: String(row?.description || ""),
    priceInPaise: Number.isFinite(Number(row?.priceInPaise)) ? Number(row.priceInPaise) : 0,
    currency: String(row?.currency || "INR").trim().toUpperCase(),
    durationDays: Number.isFinite(Number(row?.durationDays)) ? Number(row.durationDays) : 30,
    trialDurationMinutes: Number.isFinite(Number(row?.trialDurationMinutes)) ? Number(row.trialDurationMinutes) : null,
    billing: String(row?.billing || ""),
    cta: String(row?.cta || ""),
    featured: !!row?.featured,
    requiresPayment: !!row?.requiresPayment,
    contactSales: !!row?.contactSales,
    maxSystems: row?.maxSystems === null
      ? null
      : (Number.isFinite(Number(row?.maxSystems)) ? Number(row.maxSystems) : null),
    features: Array.isArray(row?.features)
      ? row.features.map((entry) => String(entry || "").trim()).filter(Boolean)
      : [],
    unavailableFeatures: Array.isArray(row?.unavailableFeatures)
      ? row.unavailableFeatures.map((entry) => String(entry || "").trim()).filter(Boolean)
      : [],
    entitlements: Array.isArray(row?.entitlements)
      ? row.entitlements.map((entry) => String(entry || "").trim().toLowerCase()).filter(Boolean)
      : [],
    updatedAt: row?.updatedAt || null,
    updatedBy: row?.updatedBy || null,
  }));

  normalized.sort((left, right) => {
    const leftIndex = PLAN_ORDER.indexOf(left.id);
    const rightIndex = PLAN_ORDER.indexOf(right.id);
    if (leftIndex >= 0 && rightIndex >= 0) return leftIndex - rightIndex;
    if (leftIndex >= 0) return -1;
    if (rightIndex >= 0) return 1;
    return left.id.localeCompare(right.id);
  });

  return normalized;
}

function normalizeEntitlementOptions(payload) {
  const options = Array.isArray(payload?.availableEntitlements) ? payload.availableEntitlements : [];
  return options
    .map((entry) => ({
      id: String(entry?.id || "").trim().toLowerCase(),
      label: String(entry?.label || "").trim(),
    }))
    .filter((entry) => entry.id);
}

function deriveEntitlementOptionsFromRows(rows = []) {
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    for (const entry of Array.isArray(row?.entitlements) ? row.entitlements : []) {
      const id = String(entry || "").trim().toLowerCase();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({ id, label: toTitleFromId(id) });
    }
  }
  return out;
}

function toggleArrayValue(values = [], nextValue) {
  if (values.includes(nextValue)) {
    return values.filter((entry) => entry !== nextValue);
  }
  return [...values, nextValue];
}

function normalizeDurationValue(rawValue) {
  return String(rawValue || "").replace(/[^\d]/g, "").slice(0, 7);
}

function minutesToDurationForm(minutes) {
  const numeric = Number.parseInt(minutes, 10);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return { value: "7", unit: "days" };
  }
  if (numeric % (24 * 60) === 0) {
    return { value: String(numeric / (24 * 60)), unit: "days" };
  }
  if (numeric % 60 === 0) {
    return { value: String(numeric / 60), unit: "hours" };
  }
  return { value: String(numeric), unit: "minutes" };
}

function durationFormToMinutes(value, unit) {
  const numeric = Number.parseInt(value, 10);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  if (unit === "days") return numeric * 24 * 60;
  if (unit === "hours") return numeric * 60;
  return numeric;
}

function buildPlanModalState(row) {
  const planIdentity = getPlanIdentity(row.id, row.name);
  return {
    planId: row.id,
    planName: planIdentity.displayName || row.name || row.id.toUpperCase(),
    publicPlanId: planIdentity.publicId,
    storedPlanId: planIdentity.storedId,
    name: row.name || "",
    description: row.description || "",
    price: paiseToUnits(row.priceInPaise),
    currency: row.currency || "INR",
    billing: row.billing || "",
    cta: row.cta || "",
    durationDays: row.durationDays ? String(row.durationDays) : "",
    maxSystems: row.maxSystems == null ? "" : String(row.maxSystems),
    allowsUnlimitedSeats: row.maxSystems == null && !isPaidSelfServePlan(row.id),
    requiresPayment: !!row.requiresPayment,
    contactSales: !!row.contactSales,
    selectedEntitlements: Array.isArray(row.entitlements) ? [...row.entitlements] : [],
  };
}

function PriceManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingPlanId, setSavingPlanId] = useState("");
  const [entitlementOptions, setEntitlementOptions] = useState([]);
  const [modal, setModal] = useState(null);
  const [trialDurationValue, setTrialDurationValue] = useState("7");
  const [trialDurationUnit, setTrialDurationUnit] = useState("days");
  const [trialSaving, setTrialSaving] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);

  const loadPricing = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const pricingResponse = await apiGet("/api/admin/plan-pricing");
      const pricingPayload = await pricingResponse.json().catch(() => null);
      if (!pricingResponse.ok) {
        toast.error(pricingPayload?.message || "Failed to load plan pricing");
        return;
      }

      const normalizedRows = normalizePlanRows(pricingPayload);
      const normalizedEntitlements = normalizeEntitlementOptions(pricingPayload);
      setRows(normalizedRows);
      setEntitlementOptions(
        normalizedEntitlements.length > 0
          ? normalizedEntitlements
          : deriveEntitlementOptionsFromRows(normalizedRows)
      );
    } catch (err) {
      console.error("Failed to load plan pricing:", err);
      toast.error("Network error while loading plan pricing");
    } finally {
      if (!silent) setLoading(false);
      if (silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  useEffect(() => {
    const freePlan = rows.find((row) => row.id === "free") || null;
    if (!freePlan) return;
    const nextForm = minutesToDurationForm(freePlan.trialDurationMinutes);
    setTrialDurationValue(nextForm.value);
    setTrialDurationUnit(nextForm.unit);
  }, [rows]);

  const entitlementLabelById = useMemo(
    () => new Map(entitlementOptions.map((entry) => [entry.id, entry.label || entry.id])),
    [entitlementOptions]
  );

  const openPlanModal = (row) => {
    if (!row || isFreePlan(row.id)) return;
    setModal(buildPlanModalState(row));
  };

  const closePlanModal = () => {
    if (savingPlanId) return;
    setModal(null);
  };

  const modalRow = useMemo(
    () => rows.find((row) => row.id === modal?.planId) || null,
    [rows, modal]
  );

  const modalPlanId = modal?.planId || "";
  const modalIsEnterprise = isEnterprisePlan(modalPlanId);
  const modalIsPaidSelfServe = isPaidSelfServePlan(modalPlanId);
  const modalPriceLocked = modalIsEnterprise;
  const modalDurationLocked = isFreePlan(modalPlanId);
  const modalModeSummary = getPlanModeLabel(modalPlanId, modal?.requiresPayment, modal?.contactSales);

  const modalEntitlementOptions = useMemo(() => {
    const out = [...entitlementOptions];
    const known = new Set(out.map((entry) => entry.id));
    for (const id of Array.isArray(modal?.selectedEntitlements) ? modal.selectedEntitlements : []) {
      if (known.has(id)) continue;
      known.add(id);
      out.push({ id, label: toTitleFromId(id) });
    }
    return out;
  }, [entitlementOptions, modal]);

  const modalSelectedEntitlementSet = useMemo(
    () => new Set(Array.isArray(modal?.selectedEntitlements) ? modal.selectedEntitlements : []),
    [modal]
  );

  const modalUnavailableEntitlements = useMemo(
    () => modalEntitlementOptions.filter((entry) => !modalSelectedEntitlementSet.has(entry.id)),
    [modalEntitlementOptions, modalSelectedEntitlementSet]
  );

  const saveTrialDuration = async () => {
    const trialDurationMinutes = durationFormToMinutes(trialDurationValue, trialDurationUnit);
    if (!trialDurationMinutes) {
      toast.error("Trial duration must be a positive value");
      return;
    }

    setTrialSaving(true);
    try {
      const response = await apiPut("/api/admin/plan-pricing/free", {
        trialDurationMinutes,
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.plan) {
        toast.error(data?.message || "Failed to update free trial duration");
        return;
      }

      const updated = normalizePlanRows({ catalog: [data.plan] })[0];
      setRows((prev) => prev.map((row) => (row.id === "free" ? updated : row)));
      toast.success(
        Number(data?.syncedTenantCount || 0) > 0
          ? `Free trial duration updated for ${Number(data.syncedTenantCount)} active tenant${Number(data.syncedTenantCount) === 1 ? "" : "s"}`
          : "Free trial duration updated"
      );
    } catch (err) {
      console.error("Failed to update free trial duration:", err);
      toast.error("Network error while updating free trial duration");
    } finally {
      setTrialSaving(false);
    }
  };

  const savePlanConfig = async () => {
    if (!modal?.planId) return;

    const currency = sanitizeCurrency(modal.currency || modalRow?.currency || "INR");
    if (!/^[A-Z]{3}$/.test(currency)) {
      toast.error("Currency must be a valid 3-letter code");
      return;
    }

    const durationDays = Number.parseInt(modal.durationDays, 10);
    if (!modalDurationLocked && (!Number.isFinite(durationDays) || durationDays <= 0)) {
      toast.error("Billing duration must be a positive whole number");
      return;
    }

    const parsedMaxSystems = Number.parseInt(modal.maxSystems || modalRow?.maxSystems || "", 10);
    const maxSystems = modalIsEnterprise || modal.allowsUnlimitedSeats ? null : parsedMaxSystems;
    if (!modalIsEnterprise && !modal.allowsUnlimitedSeats && (!Number.isFinite(maxSystems) || maxSystems <= 0)) {
      toast.error("Seat cap must be a positive whole number or unlimited");
      return;
    }

    let priceInPaise = 0;
    if (modalIsPaidSelfServe) {
      const amount = Number(modal.price);
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error("Paid plans must have a price greater than zero");
        return;
      }
      priceInPaise = Math.round(amount * PAISE_PER_UNIT);
      if (priceInPaise > 1_000_000_000) {
        toast.error("Plan price is too high");
        return;
      }
    }

    const payload = {
      name: normalizeShortText(modal.name, 80).trim(),
      description: normalizeLongText(modal.description, 260).trim(),
      priceInPaise,
      currency,
      durationDays: modalDurationLocked ? modalRow?.durationDays || 30 : durationDays,
      maxSystems,
      billing: normalizeShortText(modal.billing, 80).trim(),
      cta: normalizeShortText(modal.cta, 40).trim(),
      requiresPayment: modalIsPaidSelfServe,
      contactSales: modalIsEnterprise,
      entitlements: Array.isArray(modal.selectedEntitlements) ? modal.selectedEntitlements : [],
    };

    if (modalIsEnterprise) {
      payload.priceInPaise = 0;
      payload.requiresPayment = false;
      payload.contactSales = true;
    }

    setSavingPlanId(modal.planId);
    try {
      const response = await apiPut(`/api/admin/plan-pricing/${encodeURIComponent(modal.planId)}`, payload);
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.plan) {
        toast.error(data?.message || "Failed to update plan configuration");
        return;
      }

      const updated = normalizePlanRows({ catalog: [data.plan] })[0];
      setRows((prev) => prev.map((row) => (row.id === modal.planId ? updated : row)));

      const nextEntitlementOptions = normalizeEntitlementOptions(data);
      if (nextEntitlementOptions.length > 0) {
        setEntitlementOptions(nextEntitlementOptions);
      }

      const syncedTenantCount = Number(data?.syncedTenantCount || 0);
      toast.success(
        syncedTenantCount > 0
          ? `${updated.name || updated.id} updated for ${syncedTenantCount} active tenant${syncedTenantCount === 1 ? "" : "s"}`
          : `${updated.name || updated.id} updated`
      );
      setModal(null);
    } catch (err) {
      console.error("Failed to update plan configuration:", err);
      toast.error("Network error while updating plan configuration");
    } finally {
      setSavingPlanId("");
    }
  };

  const resetPlanConfiguration = async () => {
    if (resetSaving || savingPlanId || trialSaving) return;

    const confirmed = window.confirm(
      "Reset all pricing plans to the default catalog and clear enterprise quote overrides? This keeps subscriptions, invoices, and payments, but removes admin pricing test changes."
    );
    if (!confirmed) return;

    setResetSaving(true);
    try {
      const response = await apiPost("/api/admin/plan-pricing/reset", {});
      const data = await response.json().catch(() => null);
      if (!response.ok || !Array.isArray(data?.catalog)) {
        toast.error(data?.message || "Failed to reset plan pricing");
        return;
      }

      const normalizedRows = normalizePlanRows(data);
      const normalizedEntitlements = normalizeEntitlementOptions(data);
      setRows(normalizedRows);
      setEntitlementOptions(
        normalizedEntitlements.length > 0
          ? normalizedEntitlements
          : deriveEntitlementOptionsFromRows(normalizedRows)
      );
      setModal(null);

      const quotesDeleted = Number(data?.resetSummary?.enterpriseQuotesDeleted || 0);
      const syncedTenantCount = Number(data?.resetSummary?.syncedTenantCount || 0);
      toast.success(
        quotesDeleted > 0 || syncedTenantCount > 0
          ? `Pricing defaults restored. Cleared ${quotesDeleted} enterprise quote${quotesDeleted === 1 ? "" : "s"} and resynced ${syncedTenantCount} tenant${syncedTenantCount === 1 ? "" : "s"}.`
          : "Pricing defaults restored"
      );
    } catch (err) {
      console.error("Failed to reset plan pricing:", err);
      toast.error("Network error while resetting plan pricing");
    } finally {
      setResetSaving(false);
    }
  };

  return (
    <div className="admin-workspace pm-page">
      {/* ── Page header ── */}
      <div className="admin-shell-header">
        <div className="admin-shell-header__left">
          <h1 className="admin-shell-title">Plan Pricing &amp; Quotes</h1>
          <p className="admin-shell-sub">Billing logic, free trial control, and enterprise tenant quote setup. Public marketing copy is managed separately.</p>
        </div>
        <div className="admin-shell-header__right">
          <span className="pm-audit-badge">
            <ShieldCheck size={13} />
            Admin-only · audited
          </span>
          <button
            type="button"
            className="gc-icon-btn"
            onClick={() => loadPricing({ silent: true })}
            disabled={refreshing || resetSaving}
            data-tip={refreshing ? "Refreshing…" : "Refresh"}
          >
            <RefreshCw size={14} className={refreshing ? "spin" : ""} />
          </button>
          <button
            type="button"
            className="admin-btn-danger"
            onClick={resetPlanConfiguration}
            disabled={resetSaving || refreshing || trialSaving || !!savingPlanId}
          >
            <RotateCcw size={14} className={resetSaving ? "spin" : ""} />
            {resetSaving ? "Resetting…" : "Reset All Pricing"}
          </button>
        </div>
      </div>

      {/* ── Free Trial Control ── */}
      <div className="admin-panel pm-panel-trial">
        <div className="admin-panel-head">
          <h3 className="admin-panel-title">Free Trial Control</h3>
          <p className="admin-panel-sub">Set the live claim duration. Active trials are recalculated immediately from their original start time.</p>
        </div>
        <div className="pm-trial-row">
          <div className="admin-form-field">
            <label className="admin-form-label">Duration</label>
            <input
              type="text"
              inputMode="numeric"
              className="admin-input pm-trial-val"
              value={trialDurationValue}
              onChange={(event) => setTrialDurationValue(normalizeDurationValue(event.target.value))}
              disabled={trialSaving}
            />
          </div>
          <div className="admin-form-field">
            <label className="admin-form-label">Unit</label>
            <select
              className="admin-select"
              value={trialDurationUnit}
              onChange={(event) => setTrialDurationUnit(event.target.value)}
              disabled={trialSaving}
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
          <button
            type="button"
            className="admin-btn pm-trial-save-btn"
            onClick={saveTrialDuration}
            disabled={trialSaving}
          >
            <Save size={14} />
            {trialSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* ── Reset note ── */}
      <p className="pm-reset-note">
        Reset restores the default plan catalog and clears enterprise quote overrides. Billing history, subscriptions, and payments are not deleted.
      </p>

      {/* ── Plans table ── */}
      {loading ? (
        <div className="admin-empty">Loading pricing tiers…</div>
      ) : rows.length === 0 ? (
        <div className="admin-empty">No pricing tiers configured.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Mode</th>
                <th>Price</th>
                <th>Term</th>
                <th>Seat Cap</th>
                <th>Entitlements</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isSaving = savingPlanId === row.id;
                const isFree = isFreePlan(row.id);
                return (
                  <tr key={row.id}>
                    <td><strong>{row.displayName || row.name || row.id.toUpperCase()}</strong></td>
                    <td>{getPlanModeLabel(row.id, row.requiresPayment, row.contactSales)}</td>
                    <td>{formatPlanPrice(row.priceInPaise, row.currency, row.contactSales)}</td>
                    <td>{isFree ? "Trial" : `${row.durationDays}d`}</td>
                    <td>{getSeatCapLabel(row.maxSystems)}</td>
                    <td>
                      <span className="admin-count-badge">{(row.entitlements || []).length}</span>
                    </td>
                    <td className="admin-cell-dim">{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "Never"}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-inline-action"
                        onClick={() => openPlanModal(row)}
                        disabled={isSaving || isFree}
                        title={isFree ? "Free plan is managed via the Free Trial panel above" : "Edit plan billing config"}
                      >
                        {isFree ? "Managed above" : isSaving ? "Saving…" : "Edit"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Plan edit modal ── */}
      {modal ? (
        <div className="pm-modal-backdrop" onClick={closePlanModal}>
          <div className="pm-modal" onClick={(event) => event.stopPropagation()}>
            {/* Modal header */}
            <div className="pm-modal-head">
              <div className="pm-modal-head-copy">
                <h3>{modal.planName}: Billing Configuration</h3>
                <p>
                  These values drive server-side quote calculation and checkout. Public pricing-card copy is maintained separately.
                  {" "}
                  {modal.publicPlanId && modal.publicPlanId !== modal.storedPlanId
                    ? `Public checkout key: ${modal.publicPlanId}. Stored DB key: ${modal.storedPlanId}.`
                    : `Plan key: ${modal.publicPlanId || modal.storedPlanId || modal.planId}.`}
                </p>
              </div>
              <div className="pm-modal-head-actions">
                <span className="admin-status is-active pm-mode-pill">{modalModeSummary}</span>
                <button type="button" className="admin-btn-icon" onClick={closePlanModal} disabled={!!savingPlanId} aria-label="Close">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="pm-modal-body">
              {/* Section: Stored Plan Identity */}
              <section className="pm-modal-section">
                <h4 className="pm-modal-section-title">Stored Plan Identity</h4>
                <div className="pm-field-grid">
                  <div className="admin-form-field">
                    <label className="admin-form-label">Stored Name</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={modal.name}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, name: normalizeShortText(event.target.value, 80) } : prev))}
                      disabled={!!savingPlanId}
                    />
                    <span className="admin-form-hint">Used for the saved plan record and admin-facing views.</span>
                  </div>
                  <div className="admin-form-field pm-field-wide">
                    <label className="admin-form-label">Stored Description</label>
                    <textarea
                      className="admin-input"
                      rows={3}
                      value={modal.description}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, description: normalizeLongText(event.target.value, 260) } : prev))}
                      disabled={!!savingPlanId}
                    />
                    <span className="admin-form-hint">Marketing card copy can still override this on the public site.</span>
                  </div>
                </div>
              </section>

              {/* Section: Billing Truth */}
              <section className="pm-modal-section">
                <h4 className="pm-modal-section-title">Billing Truth</h4>
                <div className="pm-field-grid pm-field-grid--3">
                  <div className="admin-form-field">
                    <label className="admin-form-label">Base Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="admin-input"
                      value={modal.price}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, price: event.target.value } : prev))}
                      disabled={!!savingPlanId || modalPriceLocked}
                    />
                    <span className="admin-form-hint">{modalIsEnterprise ? "Controlled by the enterprise quote panel." : "Per-agent amount before GST."}</span>
                  </div>
                  <div className="admin-form-field">
                    <label className="admin-form-label">Currency</label>
                    <input
                      type="text"
                      maxLength={3}
                      className="admin-input pm-currency-input"
                      value={modal.currency}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, currency: sanitizeCurrency(event.target.value) } : prev))}
                      disabled={!!savingPlanId}
                    />
                    <span className="admin-form-hint">Three-letter ISO code for quote and order creation.</span>
                  </div>
                  <div className="admin-form-field">
                    <label className="admin-form-label">Billing Label</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={modal.billing}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, billing: normalizeShortText(event.target.value, 80) } : prev))}
                      disabled={!!savingPlanId}
                    />
                    <span className="admin-form-hint">Stored billing metadata.</span>
                  </div>
                  <div className="admin-form-field">
                    <label className="admin-form-label">CTA Label</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={modal.cta}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, cta: normalizeShortText(event.target.value, 40) } : prev))}
                      disabled={!!savingPlanId}
                    />
                    <span className="admin-form-hint">Stored action label for internal plan metadata.</span>
                  </div>
                  <div className="admin-form-field">
                    <label className="admin-form-label">Billing Term (Days)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="admin-input"
                      value={modal.durationDays}
                      onChange={(event) => setModal((prev) => (prev ? { ...prev, durationDays: normalizeIntegerInput(event.target.value, 4) } : prev))}
                      disabled={!!savingPlanId || modalDurationLocked}
                    />
                    <span className="admin-form-hint">Subscription term length used by renewals and expiry dates.</span>
                  </div>
                  <div className="admin-form-field">
                    <label className="admin-form-label">Seat Cap</label>
                    <div className="pm-seat-row">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="admin-input"
                        value={modal.maxSystems}
                        onChange={(event) => setModal((prev) => (prev ? {
                          ...prev,
                          maxSystems: normalizeIntegerInput(event.target.value, 6),
                          allowsUnlimitedSeats: false,
                        } : prev))}
                        disabled={!!savingPlanId || modal.allowsUnlimitedSeats || modalIsEnterprise}
                      />
                      <label className="pm-checkbox-label">
                        <input
                          type="checkbox"
                          checked={modal.allowsUnlimitedSeats}
                          onChange={(event) => setModal((prev) => (prev ? {
                            ...prev,
                            allowsUnlimitedSeats: event.target.checked,
                            maxSystems: event.target.checked ? "" : prev.maxSystems,
                          } : prev))}
                          disabled={!!savingPlanId || modalIsEnterprise || modalIsPaidSelfServe}
                        />
                        Unlimited
                      </label>
                    </div>
                    <span className="admin-form-hint">
                      {modalIsEnterprise
                        ? "Enterprise seat count is controlled by the tenant quote panel."
                        : "Maximum licensed agents allowed on this plan. Takes effect immediately for new purchases."}
                    </span>
                  </div>
                </div>
              </section>

              {/* Section: Checkout Policy */}
              <section className="pm-modal-section">
                <h4 className="pm-modal-section-title">Checkout Policy</h4>
                <div className="pm-policy-cards">
                  <div className="pm-policy-card">
                    <div className="pm-policy-card-label">Requires Payment</div>
                    <div className="pm-policy-card-value">{modal.requiresPayment ? "Yes" : "No"}</div>
                    <div className="admin-form-hint">{modalIsPaidSelfServe ? "Paid self-serve plans are locked to checkout-enabled mode." : "Enterprise pricing comes from tenant quotes."}</div>
                  </div>
                  <div className="pm-policy-card">
                    <div className="pm-policy-card-label">Contact Sales</div>
                    <div className="pm-policy-card-value">{modal.contactSales ? "Yes" : "No"}</div>
                    <div className="admin-form-hint">{modalIsEnterprise ? "Enterprise stays in contact-sales mode and uses quote activation." : "Self-serve paid plans are kept out of contact-sales mode."}</div>
                  </div>
                </div>
                <p className="pm-policy-note">
                  {(() => {
                    const freeCap = rows.find((r) => r.id === "free")?.maxSystems ?? 3;
                    const essentialsCap = rows.find((r) => r.id === "essentials")?.maxSystems;
                    const basicCap = rows.find((r) => r.id === "basic")?.maxSystems;
                    return `Free Trial is capped at ${freeCap} seats. Essentials at ${essentialsCap != null ? essentialsCap : "?"} seats. Pro at ${basicCap != null ? basicCap : "?"} seats. Enterprise is quote-driven — clients cannot change the quoted seat count during checkout.`;
                  })()}
                </p>
              </section>

              {/* Section: Entitlement Access */}
              <section className="pm-modal-section">
                <div className="pm-entitlement-head">
                  <div>
                    <h4 className="pm-modal-section-title">Entitlement Access</h4>
                    <p className="admin-form-hint">Choose which navigation sections this plan unlocks. Unselected items remain unavailable.</p>
                  </div>
                  <div className="pm-entitlement-actions">
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => setModal((prev) => (
                        prev ? { ...prev, selectedEntitlements: modalEntitlementOptions.map((entry) => entry.id) } : prev
                      ))}
                      disabled={!!savingPlanId}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => setModal((prev) => (prev ? { ...prev, selectedEntitlements: [] } : prev))}
                      disabled={!!savingPlanId}
                    >
                      Clear all
                    </button>
                  </div>
                </div>

                <div className="pm-entitlement-grid">
                  {modalEntitlementOptions.map((option) => (
                    <label key={`entitlement-option-${option.id}`} className="pm-check-item">
                      <input
                        type="checkbox"
                        checked={modal.selectedEntitlements.includes(option.id)}
                        onChange={() => setModal((prev) => (
                          prev
                            ? { ...prev, selectedEntitlements: toggleArrayValue(prev.selectedEntitlements, option.id) }
                            : prev
                        ))}
                        disabled={!!savingPlanId}
                      />
                      <span className="pm-check-label">{option.label || option.id}</span>
                      <code className="pm-check-code">{option.id}</code>
                    </label>
                  ))}
                </div>

                <div className="pm-entitlement-preview">
                  <div className="pm-preview-col">
                    <div className="pm-preview-col-head">Unlocked ({modal.selectedEntitlements.length})</div>
                    <ul className="pm-preview-list">
                      {modal.selectedEntitlements.length > 0 ? (
                        modal.selectedEntitlements
                          .slice(0, 8)
                          .map((id) => <li key={`include-preview-${id}`}>{entitlementLabelById.get(id) || id}</li>)
                      ) : (
                        <li className="pm-preview-empty">No unlocked navigation</li>
                      )}
                    </ul>
                  </div>
                  <div className="pm-preview-col">
                    <div className="pm-preview-col-head">Unavailable ({modalUnavailableEntitlements.length})</div>
                    <ul className="pm-preview-list">
                      {modalUnavailableEntitlements.length > 0 ? (
                        modalUnavailableEntitlements
                          .slice(0, 8)
                          .map((entry) => <li key={`exclude-preview-${entry.id}`}>{entry.label || entry.id}</li>)
                      ) : (
                        <li className="pm-preview-empty">None</li>
                      )}
                    </ul>
                  </div>
                </div>
              </section>
            </div>

            {/* Modal footer */}
            <div className="pm-modal-footer">
              <button type="button" className="admin-btn-secondary" onClick={closePlanModal} disabled={!!savingPlanId}>
                Cancel
              </button>
              <button type="button" className="admin-btn" onClick={savePlanConfig} disabled={!!savingPlanId}>
                <Save size={14} />
                {savingPlanId ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        /* ── PriceManager page-specific styles ── */
        .pm-page { padding: 24px; min-height: 100%; }

        /* Header */
        .admin-shell-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; margin-bottom: 24px; flex-wrap: wrap;
        }
        .admin-shell-header__right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; flex-wrap: wrap; }
        .admin-shell-title { font-size: 20px; font-weight: 600; color: var(--as-text); margin: 0 0 4px; }
        .admin-shell-sub { font-size: 13px; color: var(--as-text-dim); margin: 0; max-width: 560px; }
        .pm-audit-badge {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; color: var(--as-text-dim);
          background: var(--as-bg); border: 1px solid var(--as-border);
          border-radius: 20px; padding: 3px 10px;
        }

        /* Top panels */
        .pm-top-panels { display: grid; grid-template-columns: 1fr 2fr; gap: 16px; margin-bottom: 16px; }
        @media (max-width: 860px) { .pm-top-panels { grid-template-columns: 1fr; } }

        .admin-panel { background: var(--as-surface); border: 1px solid var(--as-border); border-radius: var(--as-r); padding: 20px; }
        .admin-panel-head { margin-bottom: 16px; }
        .admin-panel-title { font-size: 14px; font-weight: 600; color: var(--as-text); margin: 0 0 4px; }
        .admin-panel-sub { font-size: 12px; color: var(--as-text-dim); margin: 0; }

        /* Trial panel */
        .pm-trial-row { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
        .pm-trial-val { width: 80px; }
        .pm-trial-save-btn { align-self: flex-end; }

        /* Quote panel */
        .pm-quote-head-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
        .pm-quote-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 80px; gap: 10px; margin-bottom: 12px; }
        @media (max-width: 700px) { .pm-quote-grid { grid-template-columns: 1fr 1fr; } }
        .pm-currency-input { width: 72px; text-transform: uppercase; letter-spacing: 1px; }
        .pm-quote-info-row { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
        .pm-quote-info-pill {
          font-size: 12px; color: var(--as-text-dim);
          background: var(--as-bg); border: 1px solid var(--as-border);
          border-radius: 20px; padding: 3px 10px;
        }
        .pm-quote-info-pill--saved { color: var(--as-success); border-color: var(--as-success-bg); background: var(--as-success-bg); }
        .pm-quote-toggle-row { display: flex; align-items: center; gap: 16px; margin-bottom: 12px; }
        .pm-checkbox-label { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--as-text); cursor: pointer; user-select: none; }
        .pm-note-textarea { resize: vertical; min-height: 52px; }
        .pm-quote-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--as-border); }
        .pm-quote-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pm-meta-name { font-size: 13px; font-weight: 600; color: var(--as-text); }
        .pm-meta-dim { font-size: 12px; color: var(--as-text-dim); }

        /* Reset note */
        .pm-reset-note { font-size: 12px; color: var(--as-text-dim); margin: 0 0 16px; }

        /* admin-form helpers */
        .admin-form-field { display: flex; flex-direction: column; gap: 4px; }
        .admin-form-label { font-size: 12px; font-weight: 600; color: var(--as-text); }
        .admin-form-hint { font-size: 11px; color: var(--as-text-dim); margin: 0; }
        .admin-input {
          height: 32px; padding: 0 8px; font-size: 13px; font-family: inherit;
          background: var(--as-surface); color: var(--as-text);
          border: 1px solid var(--as-border); border-radius: var(--as-r);
          outline: none; transition: border-color .15s;
        }
        .admin-input:focus { border-color: var(--as-primary); box-shadow: 0 0 0 2px var(--as-primary-bg); }
        .admin-input[disabled] { opacity: .55; cursor: not-allowed; background: var(--as-bg); }
        .admin-input textarea, textarea.admin-input { height: auto; padding: 6px 8px; resize: vertical; }
        .admin-select {
          height: 32px; padding: 0 28px 0 8px; font-size: 13px; font-family: inherit;
          background: var(--as-surface); color: var(--as-text);
          border: 1px solid var(--as-border); border-radius: var(--as-r);
          outline: none; cursor: pointer; appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23605e5c'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 8px center;
        }
        .admin-select:focus { border-color: var(--as-primary); }
        .admin-select[disabled] { opacity: .55; cursor: not-allowed; }
        .admin-empty { padding: 40px; text-align: center; color: var(--as-text-dim); font-size: 13px; background: var(--as-surface); border: 1px solid var(--as-border); border-radius: var(--as-r); }
        .admin-count-badge {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 22px; height: 20px; padding: 0 6px;
          font-size: 11px; font-weight: 600; border-radius: 10px;
          background: var(--as-primary-bg); color: var(--as-primary);
        }
        .admin-cell-dim { color: var(--as-text-dim); font-size: 12px; }
        .admin-btn-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; border: none; border-radius: var(--as-r);
          background: transparent; color: var(--as-text-dim); cursor: pointer;
          transition: background .12s, color .12s;
        }
        .admin-btn-icon:hover { background: var(--as-bg); color: var(--as-text); }

        /* ── Modal ── */
        .pm-modal-backdrop {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(0,0,0,.35); display: flex; align-items: flex-start;
          justify-content: center; padding: 48px 16px 24px; overflow-y: auto;
        }
        .pm-modal {
          width: 100%; max-width: 780px; background: var(--as-surface);
          border-radius: 6px; box-shadow: 0 8px 32px rgba(0,0,0,.18);
          display: flex; flex-direction: column; overflow: hidden;
        }
        .pm-modal-head {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 12px; padding: 20px 24px 16px;
          border-bottom: 1px solid var(--as-border);
        }
        .pm-modal-head-copy h3 { font-size: 16px; font-weight: 600; margin: 0 0 6px; color: var(--as-text); }
        .pm-modal-head-copy p { font-size: 12px; color: var(--as-text-dim); margin: 0; }
        .pm-modal-head-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .pm-mode-pill { font-size: 11px; padding: 2px 10px; }

        .pm-modal-body { padding: 0 24px; overflow-y: auto; max-height: calc(90vh - 140px); }
        .pm-modal-section { padding: 20px 0; border-bottom: 1px solid var(--as-border); }
        .pm-modal-section:last-child { border-bottom: none; }
        .pm-modal-section-title { font-size: 13px; font-weight: 600; color: var(--as-text); margin: 0 0 14px; }

        .pm-field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .pm-field-grid--3 { grid-template-columns: 1fr 1fr 1fr; }
        .pm-field-wide { grid-column: 1 / -1; }
        @media (max-width: 600px) { .pm-field-grid, .pm-field-grid--3 { grid-template-columns: 1fr; } .pm-field-wide { grid-column: auto; } }

        .pm-seat-row { display: flex; align-items: center; gap: 10px; }

        .pm-policy-cards { display: flex; gap: 12px; margin-bottom: 10px; }
        .pm-policy-card {
          flex: 1; padding: 12px 14px;
          background: var(--as-bg); border: 1px solid var(--as-border); border-radius: var(--as-r);
        }
        .pm-policy-card-label { font-size: 11px; font-weight: 600; color: var(--as-text-dim); margin-bottom: 4px; }
        .pm-policy-card-value { font-size: 14px; font-weight: 600; color: var(--as-text); margin-bottom: 6px; }
        .pm-policy-note { font-size: 12px; color: var(--as-text-dim); margin: 0; padding: 10px 14px; background: var(--as-bg); border-radius: var(--as-r); }

        .pm-entitlement-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
        .pm-entitlement-actions { display: flex; gap: 6px; flex-shrink: 0; }
        .pm-entitlement-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 6px; margin-bottom: 16px;
        }
        .pm-check-item {
          display: flex; align-items: center; gap: 8px;
          padding: 7px 10px; border-radius: var(--as-r);
          background: var(--as-bg); border: 1px solid var(--as-border);
          cursor: pointer; transition: border-color .12s;
        }
        .pm-check-item:hover { border-color: var(--as-primary-bg); }
        .pm-check-label { flex: 1; font-size: 13px; color: var(--as-text); }
        .pm-check-code { font-size: 10px; color: var(--as-text-dim); font-family: monospace; }

        .pm-entitlement-preview {
          display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
          padding: 14px; background: var(--as-bg); border-radius: var(--as-r);
          border: 1px solid var(--as-border);
        }
        .pm-preview-col-head { font-size: 11px; font-weight: 600; color: var(--as-text-dim); margin-bottom: 8px; text-transform: uppercase; letter-spacing: .4px; }
        .pm-preview-list { margin: 0; padding-left: 16px; }
        .pm-preview-list li { font-size: 12px; color: var(--as-text); line-height: 1.8; }
        .pm-preview-empty { color: var(--as-text-dim); font-style: italic; }

        .pm-modal-footer {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 8px; padding: 16px 24px;
          border-top: 1px solid var(--as-border); background: var(--as-surface);
        }

        /* spin animation */
        @keyframes pm-spin { to { transform: rotate(360deg); } }
        .spin { animation: pm-spin .7s linear infinite; }
      `}</style>
    </div>
  );
}

export default PriceManager;
